import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_inbound_machine_learning'

const URLS = {
  MESSAGE_LOG: 'https://campus.barracuda.com/doc/96023047/'
}

function InboundMachineLearningI18n({
  classes,
  productName
}: {
  classes: ReturnType<typeof styles>
  productName?: string
}) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  return (
    <div className={classes.content} data-testid="content">
      {formatMessage('intro', { productName })}
      <br />
      {formatMessage('message_log_prefix')}
      <a href={URLS.MESSAGE_LOG} target="_blank" rel="noreferrer">
        {formatMessage('message_log_link')}
      </a>
      {formatMessage('message_log_suffix')}
      <p>
        {formatMessage('exemptions.title')}
        <ul>
          <li>
            <b>{formatMessage('exemptions.email_address_domain.title')}</b>
          </li>
          {formatMessage('exemptions.email_address_domain.description')}
          <li>
            <b>{formatMessage('exemptions.comment.title')}</b>
          </li>
          {formatMessage('exemptions.comment.description')}
          <li>
            <b>{formatMessage('exemptions.actions.title')}</b>
          </li>
          {formatMessage('exemptions.actions.description')}
        </ul>
      </p>
    </div>
  )
}

function InboundMachineLearning(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <InboundMachineLearningI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      The use of machine learning algorithms can help identify and block malicious emails before they reach your inbox,
      enhancing your email security and further protecting your organization from evolving email threats.{' '}
      {props.productName} uses machine learning algorithms by default.
      <br />
      Emails blocked by machine learning will include the threat type in the{' '}
      <a href="https://campus.barracuda.com/doc/96023047/" target="_blank" rel="noreferrer">
        {' '}
        Message Log details
      </a>
      .
      <p>
        Exempt specific email addresses or domains from machine learning threat detection:
        <ul>
          <li>
            <b>Email Address/Domain</b>
          </li>
          Add an email address or domain you want to exempt from machine learning threat detection.
          <li>
            <b>Comment</b>
          </li>
          Add an optional comment.
          <li>
            <b>Actions</b>
          </li>
          Click <b>Add</b> to add the selected email address/domain, or click <b>Remove</b> to remove an entry from the
          list.
        </ul>
      </p>
    </div>
  )
}

export default InboundMachineLearning
